// CustomerReviewsComponent.js
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getFeefoDetails } from '../../../api/apiServices';
import Image from '../../Image/Image';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';

const StarFullIcon = 'https://assets.askhippo.co.za/Icons/common/star_full.svg';
const StarEmptyIcon =
  'https://assets.askhippo.co.za/Icons/common/star_empty.svg';
const LOGO_URL = 'https://assets.askhippo.co.za/Pages/Common/FeefoLogo.svg';

const CustomerReviewsComponent = ({ data }) => {
  const { title, description, footer, stars: jsonStars } = data;

  const [headerStars, setHeaderStars] = useState(
    Math.min(5, Math.max(0, Math.round(jsonStars || 0)))
  );

  const [cardReviews, setCardReviews] = useState([
    { name: '—', stars: 5, text: 'Loading review…' },
    { name: '—', stars: 5, text: 'Loading review…' },
    { name: '—', stars: 5, text: 'Loading review…' },
  ]);

  useEffect(() => {
    let alive = true;

    const pickRandomThree = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      const copy = arr.slice();
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.slice(0, 3);
    };

    (async () => {
      try {
        const res = await getFeefoDetails();
        if (!alive) return;

        const avg = Number(res?.AverageScore) || 0;
        const rounded = Math.min(5, Math.max(0, Math.round(avg)));
        setHeaderStars(
          rounded > 0
            ? rounded
            : Math.min(5, Math.max(0, Math.round(jsonStars || 0)))
        );

        const chosen = pickRandomThree(res?.LatestReviews || []).map((r) => {
          const rawText = (r?.ReviewText || '').trim();
          return {
            name: r?.Customer || 'Anonymous',
            stars: Math.min(5, Math.max(0, Number(r?.Rating) || 0)),
            text: rawText || 'Great service and easy process.',
          };
        });

        while (chosen.length < 3) {
          chosen.push({ name: 'Anonymous', stars: 5, text: 'Great service!' });
        }

        setCardReviews(chosen);
      } catch {
        setHeaderStars(Math.min(5, Math.max(0, Math.round(jsonStars || 0))));
        setCardReviews([
          { name: 'Anonymous', stars: 5, text: 'Great service!' },
          { name: 'Anonymous', stars: 5, text: 'Quick and easy.' },
          { name: 'Anonymous', stars: 5, text: 'Highly recommended.' },
        ]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [jsonStars]);

  const renderStars = (count) => (
    <Box sx={{ display: 'flex', gap: '4px' }}>
      {[...Array(5)].map((_, i) => (
        <img
          key={i}
          src={i < count ? StarFullIcon : StarEmptyIcon}
          alt="star"
          width={25}
          height={24}
          style={{ width: '25px', height: '24px' }}
        />
      ))}
    </Box>
  );

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '1010px',
          backgroundColor: '#162F3B',
          borderRadius: '48px',
          boxSizing: 'border-box',
          textAlign: 'center',
          paddingTop: '64px',
          pb: '48px',
        }}
      >
        {/* Header stars */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <ScrollFadeWrapper
                key={i}
                delay={(i - 1) * 0.1}
                observerOptions={{ rootMargin: '0px 0px -10% 0px' }}
                twist={-30}
              >
                <img
                  src={i <= headerStars ? StarFullIcon : StarEmptyIcon}
                  alt="star"
                  width={76}
                  height={73}
                  style={{ width: '76px', height: '73px' }}
                />
              </ScrollFadeWrapper>
            ))}
          </Box>

          <Typography
            component="h3"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#FFFFFF',
              mt: '40px',
            }}
          >
            {title}
          </Typography>

          <Typography
            component="p"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              textAlign: 'center',
              color: '#FFFFFF',
              mt: '16px',
              maxWidth: '946px',
              whiteSpace: 'pre-line',
            }}
          >
            {String(description || '')
              .split(/(\*\*[^*]+\*\*)/g)
              .map((part, i) =>
                /^\*\*[^*]+\*\*$/.test(part) ? (
                  <strong key={i} style={{ fontWeight: 700 }}>
                    {part.slice(2, -2)}
                  </strong>
                ) : (
                  part
                )
              )}
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            width: '1217px',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '24px',
            mt: '56px',
          }}
        >
          {[0, 1, 2].map((idx) => (
            <ScrollFadeWrapper key={idx} delay={idx * 0.15} duration={0.7}>
              <Box
                sx={{
                  width: '389.67px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '48px',
                  padding: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                  boxShadow:
                    idx === 1
                      ? '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F'
                      : '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
                  boxSizing: 'border-box',
                }}
              >
                {renderStars(cardReviews[idx].stars)}

                <Typography
                  component="p"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.02em',
                    color: '#162F3B',
                    textAlign: 'left',
                    whiteSpace: 'pre-line',
                    width: '100%',
                  }}
                >
                  {String(cardReviews[idx].text)
                    .split(/(\*\*[^*]+\*\*)/g)
                    .map((part, i) =>
                      /^\*\*[^*]+\*\*$/.test(part) ? (
                        <strong key={i} style={{ fontWeight: 700 }}>
                          {part.slice(2, -2)}
                        </strong>
                      ) : (
                        part
                      )
                    )}
                </Typography>

                <Typography
                  component="p"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    letterSpacing: '-0.02em',
                    color: '#162F3B',
                    textAlign: 'center',
                    mt: 'auto',
                  }}
                >
                  {cardReviews[idx].name}
                </Typography>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>

        {/* Updated logo (Cloudflare R2) */}
        <Image
          src={LOGO_URL}
          alt="Company Logo"
          width={172}
          height={40}
          sx={{ width: '172px', height: 'auto', mb: '8px', mt: '48px' }}
        />

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#FFFFFF',
            textAlign: 'center',
            maxWidth: '473px',
            mx: 'auto',
            mt: '4px',
          }}
        >
          {footer}
        </Typography>
      </Box>
    </ScrollFadeSection>
  );
};

export default CustomerReviewsComponent;
